import React, { useEffect, useState } from "react";
import { applySortFilter, isEmptyFilter } from "../../utils/tableFunctions";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Grid,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import MoreMenu from "./MoreMenu";
import {
  Log,
  businessEntityAction,
  getEntityFilterConditions,
  sendDataWithPost,
} from "../../services/services";
import EditDialogs from "./Dialogs/EditDialogs";
import { StyledTableRow } from "../TableRow";
import FormSearch from "../FormSearch";
import CircularLoader from "../circularLoader";
import SearchPrompt from "../SearchPrompt";
import { useFormik } from "formik";
import FormDialog from "../FormDialog";
import { MAPPED_REGISTERED_COUNTRY, pageNumber, rowsPerPage, rowsPerPageOptions } from "../../utils/constants";
import Pagination from "../Pagination";
import Label from "../Label";
import moment from "moment";
import CommonDialog from "../CustomDialog";
import EntityDetails from "../../pages/details/entity/EntityDetails";
import HeaderHelpPage from "../HeaderHelpPage";

const defaultValue = {
  is_deleted: false
}

const TABLE_HEAD = [
  { id: "business_name", label: "Business Name" },
  { id: "registered_country", label: "Registered Country" },
  { id: "is_active", label: "Deleted" },
  { id: "next_kyc_review", label: "Next KYC review", align: "center" },
  { id: "edit" },
];

const ViewEntity = ({ userRole }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [loaded, setLoaded] = useState(false);

  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });
  const [tableData, setTableData] = useState({ results: [] });
  const [filter, setFilter] = useState({ is_active: true });
  const [inputValues, setInputValues] = useState({});

  // dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSuspendDialog, setOpenSuspendDialog] = useState(false);
  const [openUnsuspendDialog, setOpenUnsuspendDialog] = useState(false);
  const [entityFilter, setEntityFilter] = useState({});
  const [selectedId, setSelectedId] = useState();
  const [itemId, setItemId] = useState(null);
  // the list is only queried once the user has searched, so the tab opens fast
  const [searched, setSearched] = useState(false);

  const CustomTableCell = ({ align, field, id }) => {
    return (
      <TableCell
        align={align}
        onClick={() => {
          // window.open(`/3/app/entity-details/${id}/`, "_blank");
          setItemId(id);
        }}
      >
        {field}
      </TableCell>
    );
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const queryPage = (queryParams, params) => {
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `/merchant/business_entity/filter/?${queryString}`;
    loaded && setLoaded(false);
    sendDataWithPost(url, params || filter, renderData, setLoaded, () => {
      setPagination(queryParams);
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: event.target.value,
    })
  };

  const filteredList = applySortFilter(
    tableData.results || [],
    order,
    orderBy
  );

  const isItemNotFound = tableData.results.length === 0;

  const DeleteEntityDialog = ({ open, selectedId, setResponse, setLoaded }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: yup.object().shape({
        reason: yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Delete Business Entity by Id: ${selectedId}`, values);
        await businessEntityAction(
          "REMOVE",
          { selectedId, action_message: values.reason },
          handleClose
        );
      }
    });
  
    const handleClose = () => {
      setOpenDeleteDialog(false)
      resetForm();
    };

    return (
        <FormDialog
          open={open}
          onClose={handleClose}
          title="Delete this entity?"
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          maxWidth="sm"
        >
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormDialog>
    );
  };

  const SuspendEntityDialog = ({ open, selectedId }) => {
    const { handleChange, handleSubmit, handleBlur, values, touched, errors, isSubmitting, resetForm } = useFormik({
      initialValues: { reason: "" },
      validationSchema: yup.object().shape({ reason: yup.string().required("Required").trim() }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Suspend Business Entity by Id: ${selectedId}`, values);
        try {
          await businessEntityAction("Suspend", { selectedId, action_message: values.reason }, () => queryPage(pagination));
        } finally {
          handleClose();
          resetForm();
        }
      }
    });

    const handleClose = () => {
      setOpenSuspendDialog(false);
      resetForm();
    };

    return (
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Suspend this entity?"
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          maxWidth="sm"
        >
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormDialog>
    );
  };

  const UnsuspendEntityDialog = ({ open, selectedId }) => {
    const { handleChange, handleSubmit, handleBlur, values, touched, errors, isSubmitting, resetForm } = useFormik({
      initialValues: { reason: "" },
      validationSchema: yup.object().shape({ reason: yup.string().required("Required").trim() }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Unsuspend Business Entity by Id: ${selectedId}`, values);
        try {
          await businessEntityAction("Unsuspend", { selectedId, action_message: values.reason }, () => queryPage(pagination));
        } finally {
          handleClose();
          resetForm();
        }
      }
    });

    const handleClose = () => {
      setOpenUnsuspendDialog(false);
      resetForm();
    };

    return (
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Unsuspend this entity?"
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          maxWidth="sm"
        >
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormDialog>
    );
  };

  const renderData = (res) => {
    const newData = res.results.map(item => ({
      ...item,
      registered_country: item.registered_country?.registered_country
    }))

    setTableData({
      ...res,
      results: newData
    });
  }

  useEffect(() => {
    getEntityFilterConditions(setEntityFilter, setFLoaded);
  }, []);

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setOpenSuspendDialog(false);
    setOpenUnsuspendDialog(false);
  };

  // filter
  const [fLoaded, setFLoaded] = useState(false);

  const onFilterSubmit = (params) => {
    // searching with an empty form falls back to the filter the tab used to
    // load with on first render
    const fParams = isEmptyFilter(params) ? { ...defaultValue } : params;
    const newFParams = { 
      ...fParams,
      business_name: fParams.business_name?.id,
      business_name_input: inputValues?.business_name?.trim() || null,
      is_active: (fParams.is_deleted === "all" ? undefined : !fParams.is_deleted)
    };
    const newPagination = {
      ...pagination,
      page: 1,
    };

    if (newFParams.is_active === 'all') delete newFParams.is_active;
    Log.info('Click button [Apply Filter] - Search Business Entity list', fParams);

    setSearched(true);
    setFilter(newFParams);
    queryPage(newPagination, newFParams);
  };

  const schema = [
    {
      type: "search-select",
      label: "Business Name",
      param: "business_name",
      options: entityFilter?.business_name?.map((entity) => ({
        id: entity,
        label: entity?.toUpperCase()
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "select",
      label: "Registered Country",
      param: "registered_country",
      options: entityFilter?.registered_country?.map((country) => ({
        id: country.registered_country__id,
        name: MAPPED_REGISTERED_COUNTRY[country.registered_country__registered_country],
      }))
    },
    {
      type: "select",
      label: "Deleted",
      param: "is_deleted",
      id: "true",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    },
    // {
    //   type: "date",
    //   label: "Next KYC review",
    //   param: "next_kyc_review",
    // },
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {!searched ?
        <SearchPrompt />
        : loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map((row) => {
                        const {
                          id,
                          business_name,
                          registered_country,
                          is_active,
                          next_kyc_review,
                          is_suspended
                        } = row;
  
                        return (
                          <StyledTableRow
                            hover
                            key={id}
                          >
                            <CustomTableCell
                              field={business_name ? business_name.toUpperCase() : ""}
                              id={id}
                            />
                            <CustomTableCell
                              field={MAPPED_REGISTERED_COUNTRY[registered_country]}
                              id={id}
                            />
                            <CustomTableCell
                              field={
                                <Label color={!is_active ? "error" : "success"}>
                                    {!is_active ? "YES" : "NO"}
                                  </Label>
                              }
                              id={id}
                            />
                            <CustomTableCell field={next_kyc_review && moment(next_kyc_review).format("DD/MM/YYYY")} align="center" id={id} />

                            <TableCell align="right">
                              {is_active && (userRole.includes("Edit") ||
                                userRole.includes("Delete")) && (
                                <MoreMenu
                                  setOpenDeleteDialog={setOpenDeleteDialog}
                                  setOpenEditDialog={setOpenEditDialog}
                                  setOpenSuspendDialog={setOpenSuspendDialog}
                                  setOpenUnsuspendDialog={setOpenUnsuspendDialog}
                                  setRow={() => setSelectedId(id)}
                                  userRole={userRole}
                                  is_suspended={is_suspended}
                                />
                              )}
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          {openEditDialog && (
            <EditDialogs
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setTableLoaded={setLoaded}
            />
          )}
          {openDeleteDialog && (
            <DeleteEntityDialog
              open={openDeleteDialog}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
          )}
          {openSuspendDialog && (
            <SuspendEntityDialog open={openSuspendDialog} selectedId={selectedId} />
          )}
          {openUnsuspendDialog && (
            <UnsuspendEntityDialog open={openUnsuspendDialog} selectedId={selectedId} />
          )}
        </>
      )}

      {itemId && (
        <CommonDialog
          title={<HeaderHelpPage title="Business Entity Details" variant="h5" mb={0} />}
          onClose={() => setItemId(null)}
        >
          <EntityDetails itemId={itemId} />
        </CommonDialog>
      )}
    </Box>
  );
};

export default ViewEntity;
